import { SubmissionReviewStatuses } from '../../../../constants/submission-review-statuses'
import { groupBy } from 'lodash'

export const normalizeFetchSimilarSubmissionsBySolutionResponse = response =>
    response.map(submission => ({
        id: submission.file_id,
        name: submission.file_name,
        pattern: {
            name: submission.category,
            feedback: submission.feedback,
            grade: submission.grade,
            lines: submission.matched_lines,
        },
        solution: submission.submission,
    }))

export const normalizeFetchSubmissionVersionsResponse = response =>
    response.map(submission => ({
        id: submission.version_id,
        submissionId: submission.id,
        name: submission.name,
        grade: Math.round(submission.grade),
        content: submission.content,
        patterns: submission.patterns.map(pattern => ({
            id: pattern.pattern_id,
            startRow: pattern.start_row,
            endRow: pattern.end_row,
        })),
        passedUnitTestsCount: submission.tests_passed,
        failedUnitTestsCount: submission.failed_tests,
        studentFeedback: submission.student_feedback
            ? {
                  liked: submission.student_feedback.liked,
                  comment: submission.student_feedback.comment,
              }
            : null,
    }))

export const normalizeFetchSubmissionsSummaryResponse = response => ({
    submissions: response.submissions.map(submission => ({
        name: submission.file_name,
        grade: Math.round(submission.grade),
        instantFeedbackUrl: submission.feedback_url,
    })),
    totalPages: response.total_pages,
})

const normalizeReview = review => ({
    reviewer: {
        id: review.reviewed_by_user.id,
        name: review.reviewed_by_user.name,
        email: review.reviewed_by_user.email,
    },
    approveStatus: review.ai_accepted,
    submissionId: review.file_id,
    reviewedAt: review.created_at,
    oldPatternId: review.old_pattern_id,
    newPatternId: review.new_pattern_id,
    oldReasoning: Array.isArray(review.old_reasoning) ? review.old_reasoning[0] : null,
    newReasoning: Array.isArray(review.new_reasoning) ? review.new_reasoning[0] : null,
})

export const normalizeReviewSubmissionResponse = response => ({
    review: normalizeReview(response.file_review),
    newClusterId: response.new_cluster_id,
})

export const normalizeFetchSubmissionReviewsResponse = response =>
    response.map(normalizeReview).sort((a, b) => new Date(b.reviewedAt) - new Date(a.reviewedAt))

export const normalizeFetchTextAssignmentSubmissionsResponse = response =>
    response.map(submission => ({
        id: submission.id,
        studentId: submission.student_id,
        reviewStatus: submission.review_status,
        escalated: submission.escalated,
        internalNote: submission.internal_note,
    }))

export const normalizeFetchQuestionAnswersResponse = response =>
    response.map(answer => ({
        id: answer.assignment_version_student_id,
        answerId: answer.id,
        studentId: answer.student_id,
        escalated: answer.escalated,
        internalNote: answer.internal_note,
        categoryId: answer.pattern_matches[0]?.pattern_id || null,
        approveStatus: answer.pattern_matches[0]?.approved,
        actor: answer.pattern_matches[0]?.actor,
        reviewStatus: answer.pattern_matches[0]?.approved
            ? SubmissionReviewStatuses.REVIEWED
            : SubmissionReviewStatuses.UNREVIEWED,
        confidence: answer.pattern_matches[0]?.confidence_band,
        assigneeId: answer.reviewer_id,
    }))

export const normalizeFetchTextSubmissionDetailsResponse = (response, submissionId) => ({
    id: submissionId,
    feedback: response.feedback,
    answers: response.submissions
        .map(answer => ({
            id: answer.id,
            questionId: answer.question_id,
            content: answer.text,
            feedback: answer.feedback,
            escalated: answer.escalated,
            note: answer.internal_note,
            reasoning: answer.pattern_matches[0]?.reasoning,
            categoryId: answer.pattern_matches[0]?.pattern_id || null,
            approveStatus: answer.pattern_matches[0]?.approved,
            actor: answer.pattern_matches[0]?.actor,
            confidence: answer.pattern_matches[0]?.confidence_band,
            createdAt: answer.pattern_matches[0]?.created_at,
            trainingRunId: answer.pattern_matches[0]?.training_run_id,
            highlights: answer.pattern_matches[0]?.explanation?.highlights
                ? normalizeHighlights(answer.pattern_matches[0]?.explanation?.highlights)
                : null,
            explainabilityReasoning: answer.pattern_matches[0]?.explanation?.reasoning,
            ethicalCheckOutcome: answer.pattern_matches[0]?.explanation?.ethical_check_outcome,
            isAdequate: answer.pattern_matches[0]?.explanation?.is_classification_adequate ?? true,
            assigneeId: answer.reviewer_id,
        }))
        .sort((a, b) => a.questionId - b.questionId),
})

export const normalizeFetchSimilarAnswersResponse = response =>
    response.map(similarAnswer => ({
        categoryId: similarAnswer.pattern_id,
        id: similarAnswer.submission_id,
        name: similarAnswer.submission_name,
        text: similarAnswer.text,
        confidence: similarAnswer.confidence,
    }))

export const normalizeFetchAnswerCategoryHistoryResponse = response =>
    response.map(historyItem => ({
        id: historyItem.submission_id,
        categoryId: historyItem.pattern_id,
        confidence: historyItem.confidence_band,
        approveStatus: historyItem.approved,
        actor: historyItem.actor,
        deleted: historyItem.deleted,
        createdAt: historyItem.created_at,
        trainingRunId: historyItem.training_run_id,
        patternName: historyItem.pattern_name,
    }))

export const normalizeFetchAnswerCategoryMismatchPredictionsResponse = response =>
    Object.entries(response).map(([answerId, mismatch]) => ({
        id: mismatch.id,
        resolved: mismatch.resolved,
        answerId: parseInt(answerId),
        categoryScore: mismatch.pattern_score,
        currentCategoryId: mismatch.original_pattern_id,
        predictedCategoryId: mismatch.predicted_pattern_id,
    }))

export const normalizeFetchEscalatedAnswersResponse = response =>
    groupBy(
        response.map(answer => ({
            id: answer.submission_id,
            submissionId: answer.version_student_id,
            studentId: answer.student_id,
            questionId: answer.question_id,
            note: answer.internal_note,
            status: answer.status,
            user: {
                id: answer.reviewer?.id,
                name: answer.reviewer?.name,
            },
        })),
        'questionId',
    )

export const normalizeHighlights = highlights =>
    highlights.map(highlight => ({
        start: highlight.start_col,
        end: highlight.end_col,
        importance: highlight.importance ?? 1,
    }))

export const denormalizeHighlights = highlights =>
    highlights.map(highlight => ({
        start_col: highlight.start,
        end_col: highlight.end,
        importance: highlight.importance ?? 1,
    }))

export const normalizeFetchExplanationResponse = (response, args) => ({
    isAdequate: response.is_classification_adequate,
    categoryId: args?.categoryId ?? response.explained_category_id,
    reasoning: response.reasoning,
    highlights: normalizeHighlights(response.highlights),
    ethicalCheckOutcome: response.ethical_check_outcome,
})

export const normalizeFetchSubmissionAttachmentsResponse = (response) =>
    response.map(attachment => ({
        id: attachment.id,
        submissionId: attachment.assignment_version_student_id,
        questionId: attachment.question_id,
        createdAt: attachment.created_at,
        name: attachment.name,
    }))